import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePlaidLink } from 'react-plaid-link';
import { apiFetch } from '../../../shared/lib/apiClient';
import quickbooksLogo from '../../../shared/assets/logos/integrations/quickbooks.svg';
import squareLogo from '../../../shared/assets/logos/integrations/square.svg';
import stripeLogo from '../../../shared/assets/logos/integrations/stripe.svg';
import gustoLogo from '../../../shared/assets/logos/integrations/gusto.svg';
import shopifyLogo from '../../../shared/assets/logos/integrations/shopify.svg';
import hubspotLogo from '../../../shared/assets/logos/integrations/hubspot.svg';

const PlaidLogo = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect width="32" height="32" rx="6" fill="#111111" />
    <path d="M16 7L9 10.5V16L16 19.5L23 16V10.5L16 7Z" fill="white" opacity="0.9" />
    <path d="M16 19.5V25L23 21.5V16L16 19.5Z" fill="white" opacity="0.6" />
    <path d="M16 19.5V25L9 21.5V16L16 19.5Z" fill="white" opacity="0.75" />
  </svg>
);

const INTEGRATIONS = [
  { id: 'quickbooks', name: 'QuickBooks', logo: quickbooksLogo, available: true, description: 'Accounting & financial data' },
  { id: 'plaid', name: 'Plaid', logo: null, available: true, description: 'Bank accounts & transactions', customLogo: true },
  { id: 'square', name: 'Square', logo: squareLogo, available: false, description: 'Point of sale & payments' },
  { id: 'stripe', name: 'Stripe', logo: stripeLogo, available: false, description: 'Online payments' },
  { id: 'gusto', name: 'Gusto', logo: gustoLogo, available: false, description: 'Payroll & HR' },
  { id: 'shopify', name: 'Shopify', logo: shopifyLogo, available: false, description: 'E-commerce platform' },
  { id: 'hubspot', name: 'HubSpot', logo: hubspotLogo, available: false, description: 'CRM & marketing' },
];

const PlaidConnectButton = ({ onSuccess, isLoading, setIsLoading, setError }) => {
  const [linkToken, setLinkToken] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchToken = async () => {
      try {
        const res = await apiFetch('/api/plaid/create-link-token', { method: 'POST' });
        const data = await res.json();
        if (!cancelled && data.success) {
          setLinkToken(data.linkToken);
        } else if (!cancelled) {
          setError(data.error || 'Failed to initialize Plaid');
        }
      } catch {
        if (!cancelled) setError('Failed to connect to server');
      }
    };
    fetchToken();
    return () => { cancelled = true; };
  }, [setError]);

  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    setIsLoading(true);
    try {
      const res = await apiFetch('/api/plaid/exchange-token', {
        method: 'POST',
        body: JSON.stringify({
          publicToken,
          institutionId: metadata.institution?.institution_id,
          institutionName: metadata.institution?.name,
        }),
      });
      const data = await res.json();
      if (data.success) {
        onSuccess(metadata.institution?.name);
      } else {
        setError(data.error || 'Failed to connect bank account');
      }
    } catch {
      setError('Failed to connect bank account');
    } finally {
      setIsLoading(false);
    }
  }, [onSuccess, setIsLoading, setError]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: () => setIsLoading(false),
  });

  return (
    <button
      onClick={() => { setIsLoading(true); open(); }}
      disabled={!ready || isLoading}
      className="w-full py-2 text-[11px] uppercase tracking-[0.15em] text-white bg-zinc-900 hover:bg-zinc-800 rounded-md transition-colors duration-300 disabled:opacity-50"
    >
      {isLoading ? 'Connecting...' : 'Connect'}
    </button>
  );
};

const IntegrationsView = ({ loaded }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [connectedIntegrations, setConnectedIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [syncProgress, setSyncProgress] = useState(null); // { provider, status, bills, payments }

  useEffect(() => {
    if (searchParams.get('qb_connected') === 'true') {
      setSuccessMsg('QuickBooks connected — syncing your data...');
      setSearchParams({}, { replace: true });
      runInitialSync('quickbooks');
    }
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 5000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  const fetchStatus = async () => {
    try {
      const [integrationsRes, qbStatusRes, plaidStatusRes] = await Promise.all([
        apiFetch('/api/integrations'),
        apiFetch('/api/quickbooks/status'),
        apiFetch('/api/plaid/status'),
      ]);
      const integrationsData = await integrationsRes.json();
      const qbStatus = await qbStatusRes.json();
      const plaidStatus = await plaidStatusRes.json();

      const connected = {};
      if (integrationsData.integrations) {
        integrationsData.integrations.forEach(int => {
          if (int.status === 'active') {
            connected[int.provider] = true;
          }
        });
      }
      if (qbStatus.connected) {
        connected.quickbooks = true;
        connected.quickbooksCompany = qbStatus.companyName;
      }
      if (plaidStatus.connected) {
        connected.plaid = true;
        connected.plaidCompany = plaidStatus.institutionName;
      }
      setConnectedIntegrations(connected);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const runInitialSync = async (provider) => {
    setSyncProgress({ provider, status: 'syncing' });
    try {
      const res = await apiFetch(`/api/sync/${provider}`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setSyncProgress({ provider, status: 'done', bills: data.bills, payments: data.payments });
        const totalSynced = (data.bills?.synced || 0) + (data.payments?.synced || 0);
        setSuccessMsg(`QuickBooks connected — ${totalSynced} record${totalSynced !== 1 ? 's' : ''} synced`);
      } else {
        setSyncProgress({ provider, status: 'error' });
      }
    } catch {
      setSyncProgress({ provider, status: 'error' });
    }
  };

  const handleConnect = async (providerId) => {
    if (providerId === 'plaid') return; // handled by PlaidConnectButton
    if (providerId !== 'quickbooks') return;
    setActionLoading(providerId);
    setError(null);
    try {
      const response = await apiFetch('/api/quickbooks/authorize');
      const data = await response.json();
      if (data.success && data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        setError(data.message || 'Failed to initiate connection');
        setActionLoading(null);
      }
    } catch {
      setError('Failed to connect to server');
      setActionLoading(null);
    }
  };

  const handleDisconnect = async (providerId) => {
    setActionLoading(providerId);
    setError(null);
    try {
      const response = await apiFetch(`/api/${providerId}/disconnect`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        setConnectedIntegrations(prev => {
          const next = { ...prev };
          delete next[providerId];
          delete next[`${providerId}Company`];
          return next;
        });
        const name = providerId === 'plaid' ? 'Plaid' : 'QuickBooks';
        setSuccessMsg(`${name} disconnected`);
      } else {
        setError('Failed to disconnect');
      }
    } catch {
      setError('Failed to disconnect');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePlaidSuccess = (institutionName) => {
    setConnectedIntegrations(prev => ({
      ...prev,
      plaid: true,
      plaidCompany: institutionName,
    }));
    setSuccessMsg('Bank account connected successfully');
    setActionLoading(null);
  };

  return (
    <div className="max-w-[1000px] mx-auto">
      <div
        className="mb-8 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '300ms',
        }}
      >
        <h1 className="text-2xl font-light text-zinc-900 mb-1">Integrations</h1>
        <p className="text-sm text-zinc-400">
          Connect your business tools to sync data automatically
        </p>
      </div>

      {/* Success banner */}
      {successMsg && (
        <div className="mb-6 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
          <svg className="w-4 h-4 text-emerald-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm text-emerald-700">{successMsg}</span>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01" />
          </svg>
          <span className="text-sm text-red-600">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {INTEGRATIONS.map((integration, i) => {
            const isConnected = connectedIntegrations[integration.id];
            const isLoading = actionLoading === integration.id;

            return (
              <div
                key={integration.id}
                className="bg-white border border-zinc-100 rounded-lg p-5 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] hover:border-zinc-200"
                style={{
                  opacity: loaded ? 1 : 0,
                  transform: loaded ? 'translateY(0)' : 'translateY(12px)',
                  transitionDelay: `${400 + i * 80}ms`,
                }}
              >
                <div className="flex items-start justify-between mb-4">
                  {integration.customLogo ? (
                    <PlaidLogo />
                  ) : (
                    <img
                      src={integration.logo}
                      alt={integration.name}
                      className="w-8 h-8"
                    />
                  )}
                  {isConnected && (
                    <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-600">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                      Connected
                    </span>
                  )}
                </div>

                <h3 className="text-sm font-medium text-zinc-900 mb-1">{integration.name}</h3>
                <p className="text-xs text-zinc-400 mb-4">{integration.description}</p>

                {isConnected && connectedIntegrations[`${integration.id}Company`] && (
                  <p className="text-[11px] text-zinc-500 mb-3">
                    {connectedIntegrations[`${integration.id}Company`]}
                  </p>
                )}

                {syncProgress?.provider === integration.id && syncProgress.status === 'syncing' && (
                  <div className="flex items-center gap-2 mb-3 text-[11px] text-zinc-500">
                    <div className="w-3 h-3 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
                    Syncing your data...
                  </div>
                )}

                {syncProgress?.provider === integration.id && syncProgress.status === 'error' && (
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] text-red-500">Sync failed</span>
                    <button
                      onClick={() => runInitialSync(integration.id)}
                      className="text-[11px] text-zinc-500 hover:text-zinc-700 underline"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {integration.available ? (
                  isConnected ? (
                    <button
                      onClick={() => handleDisconnect(integration.id)}
                      disabled={isLoading}
                      className="w-full py-2 text-[11px] uppercase tracking-[0.15em] text-zinc-500 border border-zinc-200 hover:border-red-300 hover:text-red-500 rounded-md transition-colors duration-300 disabled:opacity-50"
                    >
                      {isLoading ? 'Disconnecting...' : 'Disconnect'}
                    </button>
                  ) : integration.id === 'plaid' ? (
                    <PlaidConnectButton
                      onSuccess={handlePlaidSuccess}
                      isLoading={isLoading}
                      setIsLoading={(v) => setActionLoading(v ? 'plaid' : null)}
                      setError={setError}
                    />
                  ) : (
                    <button
                      onClick={() => handleConnect(integration.id)}
                      disabled={isLoading}
                      className="w-full py-2 text-[11px] uppercase tracking-[0.15em] text-white bg-zinc-900 hover:bg-zinc-800 rounded-md transition-colors duration-300 disabled:opacity-50"
                    >
                      {isLoading ? 'Connecting...' : 'Connect'}
                    </button>
                  )
                ) : (
                  <div className="w-full py-2 text-center text-[11px] uppercase tracking-[0.15em] text-zinc-300 border border-zinc-100 rounded-md">
                    Coming Soon
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default IntegrationsView;
